import React, { FormEvent, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import * as yup from "yup";
import { useSnackbar } from "baseui/snackbar";
import { snackBarOverrides } from "../../components/base_ui/progress_and_validation/snackbar";
import FormControl from "../../components/base_ui/inputs/form_control";
import { Button } from "../../components/base_ui/inputs/button";
import Input from "../../components/base_ui/inputs/input";
import { StyledHeading } from "../../shared/login_and_sign_up/styled";
import { passwordRegex, passwordRegexMismatchMessage } from "../../../utils/string_utils";
import useForm from "../../shared/use_form";
import { Misuse } from "@carbon/icons-react";
import { Theme } from "baseui/styles";
import { ParagraphMedium } from "baseui/typography";

export const StyledParagraphMedium = styled(ParagraphMedium)`
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.sizing.scale800};
  color: ${({ theme }) => theme.colors.contentTertiary};

  .styled-highlighted-paragraph {
    font-weight: 700;
  }
`;

const Form = styled.form``;

const FormControlOverrides = {
  ControlContainer: {
    style: ({ $theme }: { $theme: Theme }) => {
      return {
        marginBottom: $theme.sizing.scale800,
      };
    },
  },
  Caption: {
    style: {
      "margin-bottom": "0",
    },
  },
  Label: {
    style: {
      textTransform: "none",
    },
  },
};

const schema = yup.object().shape({
  password: yup.string().required().matches(passwordRegex, passwordRegexMismatchMessage),
  confirmPassword: yup
    .string()
    .required()
    .oneOf([yup.ref("password")], "Passwords do not match"),
});

export default function SetNewPasswordContent({
  actionKey,
  title,
  description,
  setPasswordEndpointUrl,
  onSetPasswordSuccessRedirectUrl,
}: {
  actionKey: string;
  title: string;
  description?: string;
  setPasswordEndpointUrl: string;
  onSetPasswordSuccessRedirectUrl: string;
}) {
  const navigate = useNavigate();
  const { enqueue } = useSnackbar();
  const [isFetching, setIsFetching] = useState(false);

  const initialFormData = {
    password: "",
    confirmPassword: "",
  };

  const { form, values, validateForm } = useForm(initialFormData, schema, true);

  const save = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      const showErrorSnackbar = () => {
        setIsFetching(false);
        enqueue({
          message:
            "Updating user password failed, please try again or notify help@mentorcollective.org",
          startEnhancer: () => <Misuse size={24} />,
          overrides: snackBarOverrides,
        });
      };

      try {
        await validateForm();
      } catch (error) {
        return;
      }

      try {
        setIsFetching(true);

        const response = await fetch(setPasswordEndpointUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action_key: actionKey,
            password: values.password,
            password_confirmation: values.confirmPassword,
          }),
        });

        const parsedResponse = await response.json();

        setIsFetching(false);

        if (parsedResponse.isSuccess === false) {
          showErrorSnackbar();
        } else {
          navigate(onSetPasswordSuccessRedirectUrl);
        }
      } catch (error) {
        showErrorSnackbar();
      }
    },
    [
      enqueue,
      validateForm,
      setPasswordEndpointUrl,
      actionKey,
      values.password,
      values.confirmPassword,
      navigate,
      onSetPasswordSuccessRedirectUrl,
    ],
  );

  return (
    <>
      <StyledHeading>{title}</StyledHeading>
      {description && <StyledParagraphMedium>{description}</StyledParagraphMedium>}
      <Form>
        <FormControl
          label={"Enter New Password"}
          caption={
            "Must be 10 or more characters and contain at least 1 uppercase character, 1 number, and 1 special character."
          }
          error={form.password.errors.join(" ")}
          overrides={FormControlOverrides}
        >
          <Input
            required
            type="password"
            aria-label="password input"
            dataTest={"password-input"}
            value={form.password.value}
            onChange={({ currentTarget: { value } }) => form.password.set(value)}
            placeholder="Enter your new password"
          />
        </FormControl>
        <FormControl
          label={"Confirm New Password"}
          error={form.confirmPassword.errors.join(" ")}
          overrides={FormControlOverrides}
        >
          <Input
            required
            type="password"
            aria-label="confirm password input"
            dataTest={"confirm-password-input"}
            value={form.confirmPassword.value || ""}
            onChange={({ currentTarget: { value } }) => {
              form.confirmPassword.set(value);
            }}
            placeholder="Confirm your new password"
          />
        </FormControl>

        <Button
          isLoading={isFetching}
          onClick={save}
          isStretched={true}
          data-test="set-password-button"
        >
          Set New Password
        </Button>
      </Form>
    </>
  );
}
